// RevoltTooltip Component
// Reusable, viewport-clamped tooltip/popover anchored to any element via ref.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './RevoltTooltip.css';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen - Controls visibility
 * @param {object} props.anchorRef - React ref for the anchor element
 * @param {function(boolean): void} [props.onOpenChange] - Called when tooltip should open/close (required when closeDelay is set)
 * @param {number} [props.closeDelay] - When set, delays closing by this many ms when mouse leaves anchor or tooltip. Allows moving cursor between them.
 * @param {'bottom'|'top'|'left'|'right'} [props.placement='bottom'] - Placement relative to anchor
 * @param {boolean} [props.strictPlacement=false] - If true, use placement as specified without auto-flipping when out of bounds
 * @param {number} [props.offset=10] - Gap between anchor and tooltip
 * @param {number|string} [props.maxWidth=320] - Max width of tooltip
 * @param {boolean} [props.interactive=false] - If true, tooltip can receive pointer events
 * @param {string} [props.className=''] - Additional className for panel
 * @param {React.ReactNode} props.children - Tooltip content
 */
export function RevoltTooltip({
  isOpen,
  anchorRef,
  onOpenChange,
  closeDelay,
  placement = 'bottom',
  strictPlacement = false,
  offset = 10,
  maxWidth = 320,
  interactive = false,
  className = '',
  children,
}) {
  const tooltipRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    arrowLeft: 16,
    arrowTop: 16,
    resolvedPlacement: 'bottom',
  });

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    if (typeof closeDelay === 'number' && closeDelay >= 0 && onOpenChange) {
      closeTimerRef.current = setTimeout(() => onOpenChange(false), closeDelay);
    }
  }, [closeDelay, onOpenChange]);

  const computeAndSet = () => {
    const anchorEl = anchorRef?.current;
    const tooltipEl = tooltipRef.current;
    if (!anchorEl || !tooltipEl) return;

    const pad = 10;
    const rect = anchorEl.getBoundingClientRect();
    const tRect = tooltipEl.getBoundingClientRect();

    const isVertical = placement === 'top' || placement === 'bottom';
    const isHorizontal = placement === 'left' || placement === 'right';

    let top = 0;
    let left = 0;
    let arrowLeft = 16;
    let arrowTop = 16;
    let resolvedPlacement = placement;

    if (isVertical) {
      const preferred = placement === 'top' ? 'top' : 'bottom';
      resolvedPlacement = preferred;

      const topCandidate = rect.top - tRect.height - offset;
      const bottomCandidate = rect.bottom + offset;

      top = preferred === 'top' ? topCandidate : bottomCandidate;

      if (!strictPlacement) {
        if (preferred === 'bottom' && bottomCandidate + tRect.height + pad > window.innerHeight) {
          resolvedPlacement = 'top';
          top = topCandidate;
        } else if (preferred === 'top' && topCandidate < pad) {
          resolvedPlacement = 'bottom';
          top = bottomCandidate;
        }
      }

      top = clamp(top, pad, window.innerHeight - tRect.height - pad);
      left = rect.left;
      left = clamp(left, pad, window.innerWidth - tRect.width - pad);

      const anchorCenterX = rect.left + rect.width / 2;
      arrowLeft = clamp(anchorCenterX - left, 14, tRect.width - 14);
    } else if (isHorizontal) {
      const preferred = placement === 'left' ? 'left' : 'right';
      resolvedPlacement = preferred;

      const leftCandidate = rect.left - tRect.width - offset;
      const rightCandidate = rect.right + offset;

      left = preferred === 'left' ? leftCandidate : rightCandidate;

      if (!strictPlacement) {
        if (preferred === 'left' && leftCandidate < pad) {
          resolvedPlacement = 'right';
          left = rightCandidate;
        } else if (preferred === 'right' && rightCandidate + tRect.width + pad > window.innerWidth) {
          resolvedPlacement = 'left';
          left = leftCandidate;
        }
      }

      left = clamp(left, pad, window.innerWidth - tRect.width - pad);
      top = rect.top + rect.height / 2 - tRect.height / 2;
      top = clamp(top, pad, window.innerHeight - tRect.height - pad);

      const anchorCenterY = rect.top + rect.height / 2;
      arrowTop = clamp(anchorCenterY - top, 14, tRect.height - 14);
    }

    setPos({ top, left, arrowLeft, arrowTop, resolvedPlacement });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    computeAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, placement, strictPlacement, offset, maxWidth]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = () => computeAndSet();

    window.addEventListener('resize', handle);
    window.addEventListener('scroll', handle, true);

    return () => {
      window.removeEventListener('resize', handle);
      window.removeEventListener('scroll', handle, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (typeof closeDelay !== 'number' || closeDelay < 0 || !onOpenChange) return;

    const anchor = anchorRef?.current;
    if (!anchor) return;

    const handleAnchorEnter = () => {
      clearCloseTimer();
      onOpenChange(true);
    };

    const handleAnchorLeave = () => {
      scheduleClose();
    };

    anchor.addEventListener('mouseenter', handleAnchorEnter);
    anchor.addEventListener('mouseleave', handleAnchorLeave);

    return () => {
      anchor.removeEventListener('mouseenter', handleAnchorEnter);
      anchor.removeEventListener('mouseleave', handleAnchorLeave);
      clearCloseTimer();
    };
  }, [closeDelay, onOpenChange, anchorRef, scheduleClose]);

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  if (!isOpen) return null;

  const handleTooltipEnter = () => {
    clearCloseTimer();
    if (onOpenChange) onOpenChange(true);
  };

  const handleTooltipLeave = () => {
    scheduleClose();
  };

  const tooltipEl = (
    <div
      ref={tooltipRef}
      className={`revolt-tooltip ${className}`.trim()}
      data-placement={pos.resolvedPlacement}
      style={{
        top: pos.top,
        left: pos.left,
        maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
        pointerEvents: interactive ? 'auto' : 'none',
        '--revolt-tooltip-arrow-left': `${pos.arrowLeft}px`,
        '--revolt-tooltip-arrow-top': `${pos.arrowTop}px`,
      }}
      role="tooltip"
      onMouseEnter={closeDelay != null ? handleTooltipEnter : undefined}
      onMouseLeave={closeDelay != null ? handleTooltipLeave : undefined}
    >
      <div
        className={`revolt-tooltip__arrow revolt-tooltip__arrow--${pos.resolvedPlacement}`}
        aria-hidden="true"
      />
      <div className="revolt-tooltip__content">{children}</div>
    </div>
  );

  return ReactDOM.createPortal(tooltipEl, document.body);
}

